import { isVisibleArtworkObject } from './design';
import type { DesignDocument, ProductConfig } from './types';
import { deriveTransferGroups } from '../export/transfer-groups';
import { resolveProductVariant } from '../products/size-profiles';

export const TRANSFER_SHEET_PRICE_CENTS = 700;

export interface ConfiguratorPriceQuote {
  currency: ProductConfig['currency'];
  quantity: number;
  baseUnitCents: number;
  variantUnitDeltaCents: number;
  productUnitCents: number;
  productSubtotalCents: number;
  transferSheetCount: number;
  transferSheetUnitCents: number;
  printUnitCents: number;
  printSubtotalCents: number;
  configuredUnitCents: number;
  configuredSubtotalCents: number;
  printedSurfaceCount: number;
  printPricingConfigured: true;
  vatPricingConfigured: false;
  shippingPricingConfigured: false;
}

/**
 * Calculates only prices represented by the current trusted product contract.
 * Print-side surcharges and VAT are deliberately reported as unconfigured until
 * the commerce backend supplies an approved price snapshot.
 */
export function createConfiguratorPriceQuote(
  product: ProductConfig,
  design: DesignDocument,
): ConfiguratorPriceQuote {
  const variant = resolveProductVariant(product, design.variantId);
  const variantUnitDeltaCents = variant?.priceDeltaCents ?? 0;
  const productUnitCents = product.basePriceCents + variantUnitDeltaCents;
  const transferSheetCount = deriveTransferGroups(design, product).length;
  const printUnitCents = transferSheetCount * TRANSFER_SHEET_PRICE_CENTS;
  const configuredUnitCents = productUnitCents + printUnitCents;
  const printedSurfaceCount = Object.values(design.surfaces).filter(surface =>
    surface?.objects.some(isVisibleArtworkObject),
  ).length;

  return {
    currency: product.currency,
    quantity: design.quantity,
    baseUnitCents: product.basePriceCents,
    variantUnitDeltaCents,
    productUnitCents,
    productSubtotalCents: productUnitCents * design.quantity,
    transferSheetCount,
    transferSheetUnitCents: TRANSFER_SHEET_PRICE_CENTS,
    printUnitCents,
    printSubtotalCents: printUnitCents * design.quantity,
    configuredUnitCents,
    configuredSubtotalCents: configuredUnitCents * design.quantity,
    printedSurfaceCount,
    printPricingConfigured: true,
    vatPricingConfigured: false,
    shippingPricingConfigured: false,
  };
}

export function formatMoney(cents: number, currency: ProductConfig['currency'] = 'EUR'): string {
  return new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}
